import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Team = () => {
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await axios.get(`${API}/partners`);
        setPartners(response.data);
      } catch (error) {
        console.error('Error fetching partners:', error);
      }
    };
    fetchPartners();
  }, []);

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6 animate-in fade-in slide-in-from-top duration-700">
            Meet Our Expert Team
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-in fade-in slide-in-from-top duration-700 delay-100">
            Led by founder and CEO Tamika Harvey, our team of 11 certified tax professionals spans across 8 different states, bringing local expertise with nationwide reach. Each professional brings a personal touch to every client relationship. Choose the expert who is right for you.
          </p>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partners.map((partner, index) => (
              <Card key={partner.id} className={`border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 duration-300 bg-white overflow-hidden group animate-in fade-in slide-in-from-bottom duration-700 delay-${index * 50}`}>
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                  <img
                    src={partner.image}
                    alt={partner.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-6 space-y-3">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{partner.name}</h3>
                    <p className="text-teal-600 font-medium">{partner.title}</p>
                    <p className="text-sm text-gray-500 mt-1">{partner.specialty}</p>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{partner.bio}</p>
                  <Link to="/contact" state={{ selectedPartner: partner.name }}>
                    <Button variant="ghost" className="text-teal-600 hover:text-teal-700 hover:bg-teal-50 p-0 h-auto font-semibold w-full justify-start">
                      <Mail className="mr-2 h-4 w-4" />
                      Book with {partner.name.split(' ')[0]}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Team Excellence</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our team's collective expertise ensures you receive the highest quality service.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center space-y-2">
              <div className="text-5xl font-bold text-teal-600">7+</div>
              <div className="text-gray-600">Years of Excellence</div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-5xl font-bold text-teal-600">11</div>
              <div className="text-gray-600">Certified Professionals</div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-5xl font-bold text-teal-600">8</div>
              <div className="text-gray-600">States Represented</div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-5xl font-bold text-teal-600">50</div>
              <div className="text-gray-600">States Served</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl font-bold text-gray-900">
            Ready to Work with Our Team?
          </h2>
          <p className="text-xl text-gray-600">
            Choose your preferred tax preparer and book your appointment today.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-lg px-8 py-6 transition-all hover:scale-105 hover:shadow-lg">
              Book Your Appointment
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Team;
